import type { LucideIcon } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Sparkline } from "@/components/feature/sparkline";
import { cn } from "@/lib/utils";

type Tone = "good" | "bad" | "warn" | "muted";

const TONE_TEXT: Record<Tone, string> = {
  good: "text-good",
  bad: "text-bad",
  warn: "text-warn",
  muted: "text-ink-500",
};

const ICON_BG: Record<Tone, string> = {
  good: "bg-emerald-50 dark:bg-emerald-900/30 text-good",
  bad: "bg-red-50 dark:bg-red-900/30 text-bad",
  warn: "bg-orange-50 dark:bg-orange-900/30 text-warn",
  muted: "bg-brand-50 dark:bg-brand-900/30 text-brand-600 dark:text-brand-300",
};

export function KpiCard({
  label,
  value,
  delta,
  tone = "good",
  Icon,
  sub,
  spark,
  iconTone = "muted",
}: {
  label: string;
  value: string;
  delta?: string;
  tone?: Tone;
  Icon?: LucideIcon;
  sub?: string;
  spark?: number[];
  iconTone?: Tone;
}) {
  const isPos = delta?.startsWith("+") || delta?.startsWith("▲");
  const arrow = delta ? (isPos ? "▲" : "▼") : "";
  const cleanDelta = delta?.replace(/^[+▲▼]+/, "");

  return (
    <Card className="flex flex-col gap-2">
      <div className="flex items-start justify-between">
        <div className="min-w-0">
          <p className="text-xs text-ink-500">{label}</p>
          <div className="mt-1 flex items-baseline gap-2 flex-wrap">
            <h2 className="text-2xl font-bold text-ink-900 dark:text-white">{value}</h2>
            {delta && cleanDelta && (
              <span className={cn("text-xs font-semibold", TONE_TEXT[tone])}>
                {arrow} {cleanDelta}
              </span>
            )}
          </div>
          {sub && <p className="text-[11px] text-ink-500 mt-1">{sub}</p>}
        </div>
        {Icon && (
          <div className={cn("w-9 h-9 rounded-lg flex items-center justify-center shrink-0", ICON_BG[iconTone])}>
            <Icon size={16} />
          </div>
        )}
      </div>
      {spark && spark.length > 0 && (
        <div className="mt-1">
          <Sparkline data={spark} width={160} height={28} color="#2563EB" />
        </div>
      )}
    </Card>
  );
}
